import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { DynamicTemplateConfig } from '../models/dynamic-template-config.interface';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';
import { buildInvoiceNameTitle, getInvoiceName, invoiceNameTitleHeight } from './helpers/invoice-name.helper';

/**
 * Dedicated A5 Landscape PDF Invoice Template
 * Implements template configuration integration, Tamil font detection helper,
 * full-page outer border matching template.json settings, and top header layout with horizontal line.
 */
export class A5LandscapeTemplate extends BasePdfTemplate {
  private static readonly TAMIL_REGEX = /[\u0B80-\u0BFF]/;

  override templateName = 'a5-landscape';
  override templateVersion = '1.0.0';
  override description = 'A5 Landscape Invoice Template with Full Page Border';
  override pageSize: 'A5' = 'A5';

  constructor(private config?: DynamicTemplateConfig) {
    super();
  }

  /**
   * Set template configuration reference
   */
  setConfig(config: DynamicTemplateConfig): void {
    this.config = config;
  }

  /**
   * Tamil font detection function.
   */
  private getFontForText(text: string): string {
    if (text && A5LandscapeTemplate.TAMIL_REGEX.test(text)) {
      return 'NotoSansTamil';
    }
    return this.config?.themeSettings?.fontFamily || 'NotoSansTamil';
  }

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    const rawData = data as any;
    const pageSettings = this.config?.pageSettings || {
      pageSize: 'A5',
      orientation: 'landscape',
      margins: { top: 8, right: 8, bottom: 8, left: 8 },
      showBorder: true,
      borderWidth: 0.5,
      borderStyle: 'solid',
      borderColor: '#000000',
    };
    console.log(data)

    const businessLogo = data?.business?.logo || '';
    const margins = pageSettings.margins || { top: 8, right: 8, bottom: 8, left: 8 };
    const showBorder = pageSettings.showBorder !== false;
    const borderWidth = pageSettings.borderWidth || 0.5;
    const borderColor = pageSettings.borderColor || '#000000';

    // A5 Landscape Dimensions (width = 595.28, height = 419.53)
    const printableWidth = 595.28 - margins.left - margins.right;
    const printableHeight = 419.53 - margins.top - margins.bottom;

    // ── Flex-wise Top Header Layout ──────────────────────────────────────────
    // Top Left: BILL OF SUPPLY (bold, underline) + Original for receipt
    const titleLeftCol: any = {
      columns: [
        {
          text: 'BILL OF SUPPLY',
          bold: true,
          decoration: 'underline',
          fontSize: 9,
          color: '#000000',
          font: this.getFontForText('BILL OF SUPPLY'),
          width: 'auto',
        },
        {
          text: '  Original for receipt',
          fontSize: 9,
          color: '#333333',
          font: this.getFontForText('Original for receipt'),
          width: 'auto',
        },
      ],
      columnGap: 4,
      alignment: 'left',
      width: '*',
      margin: [0, 8, 0, 0]
    };

    // Top Right: Logo always top right
    const showLogoFlag = this.config?.businessDetails?.showLogo !== false;
    const logoRightCol: any = (businessLogo && showLogoFlag)
      ? {
        image: businessLogo,
        width: 60,
        alignment: 'right',
        margin: [2, 2, 0, 0],
      }
      : {
        text: '',
        width: 60,
        alignment: 'right',
      };

    const logoHeight = 30;

    const topHeaderRow: any = {
      table: {
        widths: ['*', 60],
        heights: [logoHeight], // fixed row height
        body: [
          [
            {
              stack: [titleLeftCol],
              verticalAlignment: 'middle',
            },
            logoRightCol,
          ],
        ],
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 0],
    };



    let docContent: Content[];

    //Business Details Row

    const businessHeader: any[] = [];

    const business = data.business;
    const cfg = this.config?.businessDetails;

    if (cfg?.showBusinessName && business?.name) {
      businessHeader.push({
        text: business.name,
        bold: true,
        fontSize: 16,
        alignment: 'center'
      });
    }

    if (cfg?.showTagline && business?.tagline) {
      businessHeader.push({
        text: business.tagline,
        fontSize: 10,
        alignment: 'center'
      });
    }

    if (cfg?.showAddress && business?.address) {
      businessHeader.push({
        text: business.address,
        fontSize: 9,
        alignment: 'center'
      });
    }

    const contact = [];

    if (cfg?.showPhone && business?.phone) {
      contact.push(`Mobile: ${business.phone}`);
    }

    if (cfg?.showEmail && business?.email) {
      contact.push(`Email: ${business.email}`);
    }

    if (contact.length) {
      businessHeader.push({
        text: contact.join(' | '),
        fontSize: 9,
        alignment: 'center'
      });
    }

    const tax = [];

    if (cfg?.showGSTIN && business?.gstin) {
      tax.push(`GSTIN: ${business.gstin}`);
    }

    if (cfg?.showPan && business?.pan) {
      tax.push(`PAN: ${business.pan}`);
    }

    if (cfg?.showWebsite && business?.website) {
      tax.push(business.website);
    }

    if (tax.length) {
      businessHeader.push({
        text: tax.join(' | '),
        fontSize: 9,
        alignment: 'center'
      });
    }

    const clientSaleData = (() => {
      try {
        return JSON.parse(localStorage.getItem('client_sale_data') || '{}');
      } catch {
        return {};
      }
    })();

    const msmeNo =
      business?.msmeNo ||
      (business as any)?.msmeno ||
      (business as any)?.msme_no ||
      clientSaleData?.msmeno ||
      clientSaleData?.msme_no ||
      clientSaleData?.msmeNo ||
      localStorage.getItem('msmeno') ||
      '';

    const msmeType =
      business?.msmeType ||
      (business as any)?.msmetype ||
      (business as any)?.msme_type ||
      clientSaleData?.msmetype ||
      clientSaleData?.msme_type ||
      clientSaleData?.msmeType ||
      localStorage.getItem('msmetype') ||
      '';

    const msme: string[] = [];

    const showMsmeNo = cfg ? (cfg.showMsmeNo !== false) : true;
    const showMsmeType = cfg ? (cfg.showMsmeType !== false) : true;

    if (showMsmeNo && msmeNo && msmeNo !== '-') {
      msme.push(`Msme No: ${msmeNo}`);
    }

    if (showMsmeType && msmeType && msmeType !== '-') {
      msme.push(`Msme Type: ${msmeType}`);
    }

    if (msme.length) {
      businessHeader.push({
        text: msme.join(' | '),
        fontSize: 9,
        alignment: 'center'
      });
    }

    // Height for the combined header section (topHeaderRow + businessHeader)
    // Accommodate extra MSME line if present
    // const headerSectionHeight = msme.length ? 82 : 70;
    const headerSectionHeight = 65;

    const headerSection: any = {
      table: {
        widths: ['*'],
        heights: [headerSectionHeight],
        body: [
          [
            {
              stack: [
                topHeaderRow,
                {
                  stack: businessHeader,
                  margin: [0, -20, 0, 0],
                },
              ],
              verticalAlignment: 'top',
            },
          ],
        ],
      },
      layout: {
        hLineWidth: (i: number, node: any) => (i === node.table.body.length ? borderWidth : 0),
        vLineWidth: () => 0,
        hLineColor: () => borderColor,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
      margin: [0, 0, 0, 0],
    };

    const contentList: any[] = [headerSection];

    // ── Top-level visibility flags (template.json L125-L127) ──────────────────
    const layoutSettings = (this.config as any)?.layoutSettings;
    const showPartyDetails = layoutSettings?.showPartyDetails !== false;
    const showInvoiceDetails = layoutSettings?.showInvoiceDetails !== false;

    // ── Party Details Section ─────────────────────────────────────────────────
    const partyCfg = this.config?.partyDetails as any;
    const party = data.party as any;
    const billToFields = partyCfg?.billToFields;

    const partyStack: any[] = [];

    // if (partyCfg?.showBillTo !== false) {
    //   partyStack.push({
    //     text: partyCfg?.billToPrefix || 'Buyer (Bill To)',
    //     bold: true,
    //     fontSize: 8,
    //     margin: [0, 0, 0, 2],
    //   });
    // }

    const customerName = party?.name || data?.customerName;
    if (billToFields?.showCustomerName !== false && customerName) {
      partyStack.push({
        text: customerName,
        bold: true,
        fontSize: 9,
        margin: [0, 0, 0, 1],
        font: this.getFontForText(customerName),
      });
    }

    // Left Column: Address
    const leftAddressStack: any[] = [];
    const addressParts: string[] = [];
    if (billToFields?.showCustomerAddress !== false && party?.address) addressParts.push(party.address);
    if (billToFields?.showCustomerCity !== false && party?.city) addressParts.push(party.city);
    if (billToFields?.showCustomerState !== false && party?.state) addressParts.push(party.state);
    if (billToFields?.showCustomerPin !== false && party?.pincode) addressParts.push(party.pincode);

    if (!addressParts.length && data?.customerAddress) {
      const ca = data.customerAddress;
      if (ca.line1) addressParts.push(ca.line1);
      if (ca.line2) addressParts.push(ca.line2);
      if (ca.line3) addressParts.push(ca.line3);
      if (ca.city) addressParts.push(ca.city);
      if (ca.state) addressParts.push(ca.state);
      const pinVal = ca.pincode || ca.zipCode;
      if (pinVal) addressParts.push(pinVal);
    }

    if (addressParts.length) {
      const fullAddr = addressParts.join(', ');
      leftAddressStack.push({
        text: `Address: ${fullAddr}`,
        fontSize: 8,
        margin: [0, 0, 0, 1],
        font: this.getFontForText(fullAddr),
      });
    }

    // Right Column: Remaining customer details (Mobile, Email, GSTIN, Age, Gender)
    const rightDetailsStack: any[] = [];

    // Mobile
    const phoneVal = party?.phone || party?.mobile || data?.customerMobile;
    if ((billToFields?.showCustomerPhone !== false || billToFields?.showCustomerMobile) && phoneVal) {
      rightDetailsStack.push({
        text: `Mobile: ${phoneVal}`,
        fontSize: 8,
        margin: [0, 0, 0, 1],
        noWrap: true,
        font: this.getFontForText(`Mobile: ${phoneVal}`),
      });
    }

    // Email
    const emailVal = party?.email || (data as any)?.customerEmail;
    if ((billToFields?.showCustomerEmail !== false || partyCfg?.showCustomerEmail) && emailVal) {
      rightDetailsStack.push({
        text: `Email: ${emailVal}`,
        fontSize: 8,
        margin: [0, 0, 0, 1],
        noWrap: true,
        font: this.getFontForText(`Email: ${emailVal}`),
      });
    }

    // GSTIN
    const gstinVal = party?.gstin || data?.customerGSTIN;
    if ((billToFields?.showCustomerGSTIN !== false || partyCfg?.showCustomerGSTIN) && gstinVal) {
      rightDetailsStack.push({
        text: `GSTIN: ${gstinVal}`,
        fontSize: 8,
        margin: [0, 0, 0, 1],
        noWrap: true,
        font: this.getFontForText(`GSTIN: ${gstinVal}`),
      });
    }

    // Age
    const ageVal = party?.age || (data as any)?.age || (data as any)?.customFields?.['age'] || party?.customFields?.['age'];
    const showAge = billToFields?.showCustomerAge !== false || partyCfg?.showAge || (partyCfg as any)?.pharmaFields?.showAge;
    if (ageVal || (showAge && (partyCfg as any)?.showAge)) {
      rightDetailsStack.push({
        text: `Age: ${ageVal || '-'}`,
        fontSize: 8,
        margin: [0, 0, 0, 1],
        noWrap: true,
        font: this.getFontForText(`Age: ${ageVal || '-'}`),
      });
    }

    // Gender
    const genderVal = party?.gender || (data as any)?.gender || (data as any)?.customFields?.['gender'] || party?.customFields?.['gender'];
    const showGender = billToFields?.showCustomerGender !== false || partyCfg?.showGender || (partyCfg as any)?.pharmaFields?.showGender;
    if (genderVal || (showGender && (partyCfg as any)?.showGender)) {
      rightDetailsStack.push({
        text: `Gender: ${genderVal || '-'}`,
        fontSize: 8,
        margin: [0, 0, 0, 1],
        noWrap: true,
        font: this.getFontForText(`Gender: ${genderVal || '-'}`),
      });
    }

    if (leftAddressStack.length > 0 || rightDetailsStack.length > 0) {
      partyStack.push({
        columns: [
          {
            stack: leftAddressStack.length ? leftAddressStack : [{ text: '' }],
            width: '50%',
          },
          {
            stack: rightDetailsStack.length ? rightDetailsStack : [{ text: '' }],
            width: '50%',
          },
        ],
        columnGap: 4,
      });
    }

    // ── Invoice Details Section (4 columns) ───────────────────────────────────
    const invCfg = this.config?.invoiceDetails as any;
    const invBorderWidth = invCfg?.borderWidth || borderWidth;
    const invBorderColor = invCfg?.borderColor || borderColor;

    // Collect invoice detail rows (label + value pairs)
    const invFields: Array<{ label: string; value: string }> = [];

    if (invCfg?.showInvoiceNumber !== false && data.invoiceNumber) {
      invFields.push({ label: 'Invoice No.', value: data.invoiceNumber });
    }
    if (invCfg?.showInvoiceDate !== false && data.invoiceDate) {
      invFields.push({ label: 'Invoice Date', value: String(data.invoiceDate) });
    }
    if (invCfg?.showTime && (data as any).invoiceTime) {
      invFields.push({ label: 'Time', value: (data as any).invoiceTime });
    }
    if (invCfg?.showModeOfPayment && data.paymentMethod) {
      invFields.push({ label: 'Mode of Pay', value: data.paymentMethod });
    }
    if (invCfg?.showDueDate && data.dueDate) {
      invFields.push({ label: 'Due Date', value: data.dueDate });
    }
    if (invCfg?.showPONumber && data.poNumber) {
      invFields.push({ label: 'PO No.', value: data.poNumber });
    }
    if (invCfg?.showSODCNo && data.sodcNo) {
      invFields.push({ label: invCfg?.sodcNoLabel || 'SO / DC No.', value: data.sodcNo });
    }
    if (invCfg?.showSODCDate && data.sodcDate) {
      invFields.push({ label: invCfg?.sodcDateLabel || 'SO / DC Date', value: data.sodcDate });
    }
    if (invCfg?.showReferenceNo && data.referenceNo) {
      invFields.push({ label: invCfg?.referenceNoLabel || 'Reference No.', value: data.referenceNo });
    }
    if (invCfg?.showReferenceDate && data.referenceDate) {
      invFields.push({ label: invCfg?.referenceDateLabel || 'Reference Date', value: data.referenceDate });
    }
    if (invCfg?.showEwayBillNumber && data.ewayBillNumber) {
      invFields.push({ label: 'E-Way Bill No.', value: data.ewayBillNumber });
    }
    if (invCfg?.showEwayBillDate && data.ewayBillDate) {
      invFields.push({ label: 'E-Way Bill Date', value: data.ewayBillDate });
    }
    if (invCfg?.showVehicleNumber && data.vehicleNumber) {
      invFields.push({ label: 'Vehicle No.', value: data.vehicleNumber });
    }
    if (invCfg?.showTransportName && data.transporterName) {
      invFields.push({ label: 'Transport', value: data.transporterName });
    }
    if (invCfg?.showDispatchedThrough && data.dispatchedThrough) {
      invFields.push({ label: invCfg?.dispatchedThroughLabel || 'Dispatched Through', value: data.dispatchedThrough });
    }
    if (invCfg?.showDestination && data.destination) {
      invFields.push({ label: invCfg?.destinationLabel || 'Destination', value: data.destination });
    }

    // Build invoice table body: 4 columns per row (label | value | label | value)
    const COLS = 4;
    const invRows: any[][] = [];
    for (let i = 0; i < invFields.length; i += COLS) {
      const row: any[] = [];
      for (let c = 0; c < COLS; c++) {
        const field = invFields[i + c];
        row.push(
          field
            ? {
              stack: [
                { text: field.label, bold: true, fontSize: 7, color: '#444444' },
                { text: field.value || '-', fontSize: 7 },
              ],
              margin: [2, 2, 2, 2],
            }
            : { text: '', margin: [2, 2, 2, 2] }
        );
      }
      invRows.push(row);
    }

    // If no invoice fields, add one empty row to maintain height
    if (invRows.length === 0) {
      invRows.push([{ text: '', colSpan: COLS }, {}, {}, {}]);
    }

    const invoiceDetailsTable: any = {
      table: {
        widths: Array(COLS).fill('*'),
        body: invRows,
      },
      layout: {
        hLineWidth: () => 0,  // no hLines; partyInvoiceSection bottom border provides the boundary

        vLineWidth: (i: number, node: any) =>
          i === 0 || i === node.table.widths.length
            ? 0
            : invBorderWidth,

        hLineColor: () => invBorderColor,
        vLineColor: () => invBorderColor,

        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      }
    };

    // ── Party + Invoice combined row ──────────────────────────────────────────
    // Fixed height for this combined section
    const partyInvoiceSectionHeight = 60;

    let partyInvoiceSection: any;

    if (showPartyDetails && showInvoiceDetails) {
      // Side by side: party left (40%), invoice right (60%)
      partyInvoiceSection = {
        table: {
          widths: ['45%', '55%'],
          heights: [partyInvoiceSectionHeight],
          body: [
            [
              {
                stack: partyStack.length ? partyStack : [{ text: '' }],
                verticalAlignment: 'top',
                margin: [2, 2, 2, 2],
              },
              {
                stack: [invoiceDetailsTable],
                verticalAlignment: 'top',
                margin: [0, 0, 0, 0],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === node.table.body.length ? borderWidth : 0),
          vLineWidth: (i: number, node: any) => {
            if (i === 1) return borderWidth; // separator
            return 0;
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
      };
    } else if (showPartyDetails && !showInvoiceDetails) {
      // Party details full width
      partyInvoiceSection = {
        table: {
          widths: ['*'],
          heights: [partyInvoiceSectionHeight],
          body: [
            [{ stack: partyStack.length ? partyStack : [{ text: '' }], verticalAlignment: 'top', margin: [2, 2, 2, 2] }],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === node.table.body.length ? borderWidth : 0),
          vLineWidth: (i: number, node: any) => {
            if (i === 1) return borderWidth; // separator
            return 0;
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
      };
    } else if (!showPartyDetails && showInvoiceDetails) {
      // Invoice details full width
      partyInvoiceSection = {
        table: {
          widths: ['*'],
          heights: [partyInvoiceSectionHeight],
          body: [
            [{ stack: [invoiceDetailsTable], verticalAlignment: 'top', margin: [0, 0, 0, 0] }],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === node.table.body.length ? borderWidth : 0),
          vLineWidth: (i: number, node: any) => {
            if (i === 1) return borderWidth; // separator
            return 0;
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
      };
    }

    // ── Items Table Section ───────────────────────────────────────────────────
    const itemsCfg = (this.config?.itemTable || (this.config as any)?.itemsTable || {}) as any;
    const items = data.items || [];

    // Active Columns determination
    let activeCols: Array<{ key: string; label: string; width: string; align: string }> = [];

    if (Array.isArray(itemsCfg.columns) && itemsCfg.columns.length > 0) {
      activeCols = itemsCfg.columns
        .filter((c: any) => c.show !== false)
        .map((c: any) => ({
          key: c.key,
          label: c.label || c.key,
          width: c.width || 'auto',
          align: c.align || 'left',
        }));
    }

    // Fallback if activeCols is empty
    if (activeCols.length === 0) {
      if (itemsCfg.showSerialNumber !== false) activeCols.push({ key: 'sno', label: 'S.No', width: 'auto', align: 'center' });
      if (itemsCfg.showProductName !== false) activeCols.push({ key: 'name', label: 'Item Description', width: '*', align: 'left' });
      if (itemsCfg.showHSN !== false) activeCols.push({ key: 'hsn', label: 'HSN/SAC', width: 'auto', align: 'center' });
      if (itemsCfg.showBatchNo) activeCols.push({ key: 'batch', label: 'Batch No.', width: 'auto', align: 'center' });
      if (itemsCfg.showMfgDate) activeCols.push({ key: 'mfgDate', label: 'Mfg Date', width: 'auto', align: 'center' });
      if (itemsCfg.showExpDate) activeCols.push({ key: 'expDate', label: 'Exp Date', width: 'auto', align: 'center' });
      if (itemsCfg.showMRP || itemsCfg.showMrp) activeCols.push({ key: 'mrp', label: 'MRP', width: 'auto', align: 'right' });
      if (itemsCfg.showQuantity !== false) activeCols.push({ key: 'quantity', label: 'Qty', width: 'auto', align: 'center' });
      if (itemsCfg.showUnit !== false) activeCols.push({ key: 'unit', label: itemsCfg.unitLabel || 'Unit', width: 'auto', align: 'center' });
      if (itemsCfg.showPrice !== false) activeCols.push({ key: 'price', label: 'Rate', width: 'auto', align: 'right' });
      if (itemsCfg.showDiscountPercent) activeCols.push({ key: 'discountPercent', label: 'Disc %', width: 'auto', align: 'center' });
      if (itemsCfg.showDiscountAmount) activeCols.push({ key: 'discountAmount', label: 'Disc Amt', width: 'auto', align: 'right' });
      if (itemsCfg.showTaxableAmount) activeCols.push({ key: 'taxableAmount', label: 'Taxable Amt', width: 'auto', align: 'right' });
      if (itemsCfg.showTaxPercent) activeCols.push({ key: 'taxPercent', label: 'Tax %', width: 'auto', align: 'center' });
      if (itemsCfg.showTaxAmount !== false) activeCols.push({ key: 'taxAmount', label: 'Tax Amt', width: 'auto', align: 'right' });
      if (itemsCfg.showAmount !== false) activeCols.push({ key: 'amount', label: 'Amount', width: 'auto', align: 'right' });
    }

    // Ensure item name column is present
    const hasNameCol = activeCols.some(c => c.key === 'name' || c.key === 'product' || c.key === 'item');
    if (!hasNameCol) {
      const insertIdx = activeCols.findIndex(c => c.key === 'sno' || c.key === 'hsn');
      activeCols.splice(insertIdx >= 0 ? insertIdx + 1 : 0, 0, {
        key: 'name',
        label: 'Item Description',
        width: '*',
        align: 'left',
      });
    }

    // Ensure flex width '*' column exists for 100% table width stretching
    const hasFlexCol = activeCols.some(c => c.width === '*');
    if (!hasFlexCol) {
      const nameCol = activeCols.find(c => c.key === 'name' || c.key === 'product' || c.key === 'item');
      if (nameCol) {
        nameCol.width = '*';
      } else if (activeCols.length > 1) {
        activeCols[1].width = '*';
      }
    }

    // Header styling
    const headerStyle = itemsCfg.headerStyle || {};
    const headerBg = headerStyle.backgroundColor && headerStyle.backgroundColor !== '#FFFFFF' ? headerStyle.backgroundColor : undefined;
    const headerTextColor = headerStyle.textColor || '#000000';
    const headerFontSize = headerStyle.fontSize || 8;
    const headerBold = headerStyle.bold !== false;

    // Body styling
    const bodyStyle = itemsCfg.bodyStyle || {};
    const bodyFontSize = bodyStyle.fontSize || 8;
    const alternateRowColor = bodyStyle.alternateRowColor || undefined;

    // ── Fixed row height config ────────────────────────────────────────────────
    const FIXED_ITEM_ROWS = 5;
    const HEADER_ROW_HEIGHT = 15;
    const ITEM_ROW_HEIGHT = 15;
    const TOTAL_ROW_HEIGHT = 10;
    const GRAND_TOTAL_ROW_HEIGHT = 10;

    // ── Compute overall totals (across ALL items) ─────────────────────────────
    let totalQuantity = 0;
    let totalSubAmount = 0;
    let totalNetAmount = 0;
    let totalDiscountAmount = 0;
    let totalFreeQty = 0;
    let totalFreeAmount = 0;

    items.forEach((item: any) => {
      // totalQuantity += item.is_free ? 0 : Number(item.quantity || 0);
      // T.Qty includes free qty; only amounts exclude free items
      totalQuantity += Number(item.quantity || 0);
      totalSubAmount += item.is_free ? 0 : Number(item.amount || 0);
      totalNetAmount += item.is_free ? 0 : Number(item.netValue || item.total || item.amount || 0);
      totalDiscountAmount += item.is_free ? 0 : Number(item.discountAmount ?? item.discountamt ?? 0);
      if (Number(item.is_free) === 1) {
        totalFreeQty += Number(item.quantity || 0);
        totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
      }
    });

    // ── Helper: build one item row ─────────────────────────────────────────────
    const buildItemRow = (item: any, idx: number, globalIdx: number): any[] => {
      const isAlt = globalIdx % 2 === 1;
      const rowFill = isAlt && alternateRowColor ? alternateRowColor : undefined;

      return activeCols.map(col => {
        let valStr = '';
        switch (col.key) {
          case 'sno': valStr = String(item.sno !== undefined ? item.sno : globalIdx + 1); break;
          case 'name':
          case 'product':
          case 'item':
            valStr = item.name || item.productName || item.description || '';
            if (itemsCfg.showRefName && item.product?.reference_name) valStr += ` (${item.product.reference_name})`;
            break;
          case 'hsn':
          case 'hsncode': valStr = String(item.hsn || item.hsncode || ''); break;
          case 'quantity': valStr = String(item.quantity !== undefined ? item.quantity : ''); break;
          // case 'unit': valStr = String(item.unit?.name || ''); break;
          // Unit: unit object → product.units (sale item API) → plain unit string
          case 'unit':
            valStr = getItemUnitName(item);
            break;
          case 'mfgDate': valStr = String(item.mfgDate || ''); break;
          case 'expDate': valStr = String(item.expDate || ''); break;
          case 'mrp': valStr = item.mrp != null ? Number(item.mrp).toFixed(2) : ''; break;
          case 'price':
          case 'rate': valStr = item.price != null ? Number(item.price).toFixed(2) : ''; break;
          case 'amount': valStr = item.is_free ? '0.00' : (item.amount != null ? Number(item.amount).toFixed(2) : '0.00'); break;
          case 'batch': valStr = String(item.batch || ''); break;
          case 'discountPercent':
          case 'discountPercentage':
            valStr = item.is_free ? '0.00' : (item.discountPercent !== undefined ? `${item.discountPercent}%`
              : (item.discountPercentage !== undefined ? `${item.discountPercentage}%`
                : (item.discount ? `${item.discount}%` : '0%')));
            break;
          case 'discountAmount':
          case 'discountamt':
            valStr = item.is_free ? '0.00' : (item.discountAmount !== undefined ? Number(item.discountAmount).toFixed(2)
              : (item.discountamt ? Number(item.discountamt).toFixed(2) : '0.00'));
            break;
          case 'taxableAmount': valStr = item.is_free ? '0.00' : (item.taxableAmount !== undefined ? Number(item.taxableAmount).toFixed(2) : ''); break;
          case 'taxPercent':
          case 'taxRate':
            valStr = item.is_free ? '0.00' : (item.taxPercent !== undefined ? `${item.taxPercent}%`
              : (item.taxRate ? `${item.taxRate}%` : (item.tax ? `${item.tax}%` : '0%')));
            break;
          case 'taxAmount':
          case 'taxamount':
            valStr = item.is_free ? '0.00' : (item.taxAmount !== undefined ? Number(item.taxAmount).toFixed(2)
              : (item.taxamount ? Number(item.taxamount).toFixed(2) : '0.00'));
            break;
          case 'total':
          case 'netValue':
            valStr = item.is_free ? '0.00' : (item.netValue !== undefined ? Number(item.netValue).toFixed(2)
              : (item.total ? Number(item.total).toFixed(2) : '0.00'));
            break;
          default: valStr = String(item[col.key] !== undefined ? item[col.key] : ''); break;
        }

        // ── FREE badge: shown inline in name column when is_free === 1 ──────────
        const isNameCol = col.key === 'name' || col.key === 'product' || col.key === 'item';
        const isFreeItem = Number(item.is_free) === 1;

        // Selected variants / parameters ("label : value") as one compact line below the name —
        // single line keeps the fixed 5-row item table within the page
        const mappingLine = isNameCol ? getSelectedMappingLines(item).join(', ') : '';

        if (isNameCol && (isFreeItem || mappingLine)) {
          const nameLine: any = isFreeItem
            ? {
              text: [
                { text: valStr, font: this.getFontForText(valStr) },
                { text: ' [FREE]', bold: true, color: '#000', fontSize: Math.max(bodyFontSize - 1, 5) },
              ],
            }
            : { text: valStr, font: this.getFontForText(valStr) };
          return {
            stack: [
              nameLine,
              ...(mappingLine
                ? [{ text: mappingLine, fontSize: Math.max(bodyFontSize - 1.5, 5), font: this.getFontForText(mappingLine) }]
                : []),
            ],
            fontSize: bodyFontSize,
            alignment: col.align || 'left',
            fillColor: rowFill,
          };
        }

        // if (isNameCol && isFreeItem) {
        //   return {
        //     text: [
        //       { text: valStr, font: this.getFontForText(valStr) },
        //       { text: ' [FREE]', bold: true, color: '#000', fontSize: Math.max(bodyFontSize - 1, 5) },
        //     ],
        //     fontSize: bodyFontSize,
        //     alignment: col.align || 'left',
        //     fillColor: rowFill,
        //   };
        // }

        return {
          text: valStr,
          fontSize: bodyFontSize,
          alignment: col.align || 'left',
          fillColor: rowFill,
          font: this.getFontForText(valStr),
        };
      });
    };

    // ── Helper: build the table layout definition ──────────────────────────────
    const itemsTableLayoutMode = (this.config as any)?.layoutSettings?.itemsTableLayout || 'standard';
    const itemsBorderColor = bodyStyle.borderColor || borderColor;

    const buildTableLayout = (includeTotals: boolean, hasFreeRow: boolean = false): any => {
      // When totals are included: Total at FIXED_ITEM_ROWS+1, Grand Total at FIXED_ITEM_ROWS+2, Free row at FIXED_ITEM_ROWS+3
      const totalRowLineIdx = FIXED_ITEM_ROWS + 1;
      const grandTotalRowLineIdx = FIXED_ITEM_ROWS + 2;
      const freeRowLineIdx = FIXED_ITEM_ROWS + 3;

      if (itemsTableLayoutMode === 'bordered') {
        return {
          hLineWidth: () => borderWidth,
          vLineWidth: () => borderWidth,
          hLineColor: () => itemsBorderColor,
          vLineColor: () => itemsBorderColor,
          paddingLeft: () => 3, paddingRight: () => 3,
          paddingTop: () => 2, paddingBottom: () => 2,
        };
      } else if (itemsTableLayoutMode === 'compact') {
        return {
          hLineWidth: (i: number, node: any) => {
            if (i === 0 || i === 1) return borderWidth;
            if (i === node.table.body.length) return borderWidth;
            if (includeTotals && (i === totalRowLineIdx || i === grandTotalRowLineIdx)) return borderWidth;
            if (includeTotals && hasFreeRow && i === freeRowLineIdx) return 1.5;
            return 0;
          },
          vLineWidth: () => 0,
          hLineColor: () => itemsBorderColor,
          paddingLeft: () => 3, paddingRight: () => 3,
          paddingTop: () => 2, paddingBottom: () => 2,
        };
      } else {
        // standard
        return {
          hLineWidth: (i: number, node: any) => {
            if (i === 0 || i === 1) return borderWidth;
            if (i === node.table.body.length) return borderWidth;
            if (includeTotals && (i === totalRowLineIdx || i === grandTotalRowLineIdx)) return borderWidth;
            if (includeTotals && hasFreeRow && i === freeRowLineIdx) return 1.5;
            return 0;
          },
          vLineWidth: (i: number, node: any) => {
            if (i === 0 || i === node.table.widths.length) return 0;
            return borderWidth;
          },
          hLineColor: () => itemsBorderColor,
          vLineColor: () => itemsBorderColor,
          paddingLeft: () => 3, paddingRight: () => 3,
          paddingTop: () => 2, paddingBottom: () => 2,
        };
      }
    };

    // ── Table Header Row (reused on every page) ────────────────────────────────
    const tableHeaderRow: any[] = activeCols.map(col => ({
      text: col.label,
      bold: headerBold,
      fontSize: headerFontSize,
      color: headerTextColor,
      fillColor: headerBg,
      alignment: col.align || 'left',
    }));

    // ── Helper: build items table for a chunk ──────────────────────────────────
    const buildChunkTable = (chunkItems: any[], globalStartIdx: number, includeTotals: boolean): any => {
      const rows: any[][] = [JSON.parse(JSON.stringify(tableHeaderRow))];

      // Add item rows for this chunk
      chunkItems.forEach((item, idx) => {
        rows.push(buildItemRow(item, idx, globalStartIdx + idx));
      });

      // Pad empty rows up to FIXED_ITEM_ROWS
      const paddingNeeded = Math.max(0, FIXED_ITEM_ROWS - chunkItems.length);
      for (let p = 0; p < paddingNeeded; p++) {
        rows.push(activeCols.map(() => ({ text: '', fontSize: bodyFontSize })));
      }

      const hasFreeItems = totalFreeQty > 0;

      if (includeTotals && itemsCfg.showTotalQuantityRow !== false) {
        const hasDiscCol = activeCols.some(c => c.key === 'discountAmount' || c.key === 'discountamt');

        // TOTAL row
        rows.push(activeCols.map(col => {
          let text = '';
          let align = col.align || 'left';
          if (col.key === 'sno') { text = ''; }
          else if (col.key === 'name' || col.key === 'product' || col.key === 'item') { text = 'Total'; align = 'right'; }
          else if (col.key === 'quantity') { text = String(totalQuantity); }
          else if (col.key === 'discountAmount' || col.key === 'discountamt') {
            text = hasDiscCol ? ` ${totalDiscountAmount.toFixed(2)}` : '';
          } else if (col.key === 'amount') {
            text = ` ${totalSubAmount.toFixed(2)}`;
          }
          return { text, bold: true, fontSize: bodyFontSize, alignment: align };
        }));

        // GRAND TOTAL row — free qty & free amount shown on the SAME line when present
        const lastColIdx = activeCols.length - 1;
        const grandLabelColIdx = activeCols.length - 2;
        rows.push(activeCols.map((col, colIdx) => {
          let text = '';
          let align = col.align || 'left';
          if (colIdx === grandLabelColIdx) { text = 'Grand Total :'; align = 'right'; }
          else if (colIdx === lastColIdx) { text = `\u20B9 ${totalNetAmount.toFixed(2)}`; align = 'right'; }
          else if (hasFreeItems && col.key === 'quantity') { text = String(totalFreeQty); align = 'center'; }
          else if (hasFreeItems && col.key === 'amount') { text = totalFreeAmount.toFixed(2); align = col.align || 'right'; }
          else if (hasFreeItems && (col.key === 'name' || col.key === 'product' || col.key === 'item')) { text = 'Free Items :'; align = 'right'; }
          return { text, bold: true, fontSize: bodyFontSize, alignment: align };
        }));
      }

      // Heights: 1 header + FIXED_ITEM_ROWS items + total rows
      const heights: number[] = [
        HEADER_ROW_HEIGHT,
        ...Array(FIXED_ITEM_ROWS).fill(ITEM_ROW_HEIGHT),
        ...(includeTotals ? [TOTAL_ROW_HEIGHT, GRAND_TOTAL_ROW_HEIGHT] : []),
      ];

      return {
        table: {
          widths: activeCols.map(c => c.width),
          headerRows: 1,
          heights: (rowIndex: number) => heights[rowIndex] ?? ITEM_ROW_HEIGHT,
          body: rows,
        },
        layout: buildTableLayout(includeTotals, hasFreeItems),
        margin: [0, 0, 0, 0],
      };
    };

    // ── Footer builder ─────────────────────────────────────────────────────────
    const footer = (this.config as any)?.footer || {};
    const currSymbol = (this.config as any)?.additionalSettings?.currencySymbol || '₹';

    const showPrevBalance = footer.showPreviousBalance !== false;
    const showReceived = footer.showReceivedAmount !== false;
    const showCurrBalance = footer.showCurrentBalance !== false;
    const prevBal = Number(rawData.previousBalance ?? rawData.party?.balance ?? 0);
    const received = Number(rawData.paidamount ?? rawData.receivedAmount ?? 0);
    const currBal = Number(rawData.balanceamount ?? rawData.currentBalance ?? 0);

    const makeBalCell = (label: string, value: number, show: boolean): any => ({
      stack: show ? [{
        columns: [
          { text: label, bold: true, fontSize: 7, color: '#000', width: '*' },
          { text: `${currSymbol}${value.toFixed(2)}`, fontSize: 7, bold: true, alignment: 'right', width: 'auto' },
        ],
        columnGap: 2,
      }] : [],
      margin: [4, 3, 4, 3],
    });

    const buildFooterElements = (bottomPanelHeight?: number): any[] => {
      const elems: any[] = [];

      // Balance strip
      const balanceCells = [
        makeBalCell('Previous Balance:', prevBal, showPrevBalance),
        makeBalCell('Received Amount:', received, showReceived),
        makeBalCell('Current Balance:', currBal, showCurrBalance),
      ];
      elems.push({
        table: {
          widths: ['*', '*', '*'],
          body: [[balanceCells[0], balanceCells[1], balanceCells[2]]],
        },
        layout: {
          hLineWidth: (i: number) => i === 0 ? borderWidth : 0,
          vLineWidth: (i: number, node: any) => (i === 0 || i === node.table.widths.length) ? 0 : borderWidth,
          hLineColor: () => borderColor, vLineColor: () => borderColor,
          paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
      });

      // Amount in Words strip
      const totalsCfg = (this.config?.totalsSection || (this.config as any)?.totals || {}) as any;
      const showAmountInWords = totalsCfg?.showAmountInWords !== false;
      const rawInWords = data.amountInWords || (totalNetAmount > 0 ? this.numberToWords(totalNetAmount) : '');
      if (showAmountInWords && rawInWords) {
        const prefix = totalsCfg.amountInWordsPrefix !== undefined ? totalsCfg.amountInWordsPrefix : 'Rupees: ';
        const suffix = totalsCfg.amountInWordsSuffix !== undefined ? totalsCfg.amountInWordsSuffix : ' Only';
        const fullInWords = `${prefix}${rawInWords}${suffix}`.replace(/\s+/g, ' ').trim();
        elems.push({
          table: {
            widths: ['*'],
            body: [
              [
                {
                  columns: [
                    { text: 'Amount in Words:', bold: true, fontSize: 7, color: '#000', width: 'auto' },
                    { text: ` ${fullInWords}`, fontSize: 7, italics: true, color: '#000', width: '*' },
                  ],
                  margin: [4, 2, 4, 2],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: (i: number) => i === 0 ? borderWidth : 0,
            vLineWidth: () => 0,
            hLineColor: () => borderColor,
            paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
          },
          margin: [0, 0, 0, 0],
        });
      }

      // Notes strip
      const showNotes = footer.showNotes === true;
      const notesText = rawData.notesText || rawData.notes || footer.notesText || '';
      if (showNotes && notesText) {
        elems.push({
          table: {
            widths: ['*'],
            body: [
              [
                {
                  columns: [
                    { text: 'Notes:', bold: true, fontSize: 7, color: '#000', width: 'auto' },
                    { text: ` ${notesText}`, fontSize: 7, color: '#333', width: '*' },
                  ],
                  margin: [4, 2, 4, 2],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: (i: number) => i === 0 ? borderWidth : 0,
            vLineWidth: () => 0,
            hLineColor: () => borderColor,
            paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
          },
          margin: [0, 0, 0, 0],
        });
      }

      // Bottom panel (bank+QR | terms | signature)
      const showBankDetails = footer.showBankDetails === true;
      const showQRCode = footer.showQRCode === true;
      const showTerms = footer.showTermsAndConditions === true;
      const showSignature = footer.showSignature === true;
      const showBusinessName = footer.showForBusinessName === true;

      const bankData = rawData.bankDetails || {};
      const qrImage = rawData.qrcode || rawData.qrCodeData || footer.qrCodeImage || null;
      const termsText = rawData.termsAndConditions || rawData.terms || footer.termsText || '';
      const sigImage = rawData.signature || rawData.signatureImage || footer.signatureImage || footer.signature || null;
      const sigLabel = footer.signatureLabel || 'Authorized Signatory';
      const businessName = rawData.business?.name || rawData.businessName || '';

      // Bank panel
      const bankStack: any[] = [];
      if (showBankDetails) {
        if (bankData.bankName) bankStack.push({ text: `Name: ${bankData.bankName}`, fontSize: 7 });
        if (bankData.ifscCode) bankStack.push({ text: `IFSC: ${bankData.ifscCode}`, fontSize: 7 });
        if (bankData.accountNumber) bankStack.push({ text: `Acc No: ${bankData.accountNumber}`, fontSize: 7 });
        if (bankData.accountHolderName) bankStack.push({ text: `A/C Holder: ${bankData.accountHolderName}`, fontSize: 7 });
        if (bankData.upiId) bankStack.push({ text: `UPI: ${bankData.upiId}`, fontSize: 7 });
      }
      const bankQrCols: any[] = [];
      if (bankStack.length > 0) bankQrCols.push({ stack: bankStack, width: '*' });
      if (showQRCode && qrImage) {
        bankQrCols.push({ stack: [{ image: qrImage, width: 40, alignment: 'center' }], width: 40, alignment: 'center' });
      }
      const bankPanel = (showBankDetails || (showQRCode && qrImage))
        ? { columns: bankQrCols, columnGap: 4, margin: [4, 4, 4, 4] } : null;

      // Terms panel
      const termsStack: any[] = [];
      if (showTerms && termsText) {
        termsStack.push({ text: 'Terms & Conditions:', bold: true, fontSize: 7, color: '#000', margin: [0, 0, 0, 2] });
        termsText.split('\n').filter((p: string) => p.trim() !== '').forEach((p: string, i: number) => {
          termsStack.push({ text: `${i + 1}. ${p.trim().replace(/^\d+[\.\)]\s*/, '')}`, fontSize: 6, margin: [2, 1, 0, 0] });
        });
      }
      const termsPanel = termsStack.length > 0 ? { stack: termsStack, margin: [4, 4, 4, 4] } : null;

      // Signature panel
      const signPanel: any[] = [];
      if (showSignature) {
        signPanel.push({ text: sigLabel, bold: true, fontSize: 7, alignment: 'center', margin: [0, 0, 0, 4] });
        if (sigImage) {
          signPanel.push({ image: sigImage, width: 30, alignment: 'center', margin: [0, 0, 0, 2] });
        } else {
          signPanel.push({ text: '', margin: [0, 5, 0, 0] });
        }
        signPanel.push({ canvas: [{ type: 'line', x1: 4, y1: 0, x2: 80, y2: 0, lineWidth: 0.5 }], margin: [0, 0, 0, 2] });
      }
      if (showBusinessName && businessName) signPanel.push({ text: businessName, bold: true, fontSize: 7, alignment: 'center' });
      const signPanelContent = signPanel.length > 0 ? { stack: signPanel, margin: [4, 4, 4, 4], alignment: 'center' } : null;

      const visiblePanels: any[] = [bankPanel, termsPanel, signPanelContent].filter(Boolean);
      if (visiblePanels.length > 0) {
        const bottomTable: any = {
          table: {
            widths: visiblePanels.map(() => '*'),
            body: [visiblePanels],
          },
          layout: {
            hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? borderWidth : 0,
            vLineWidth: (i: number, node: any) => (i === 0 || i === node.table.widths.length) ? 0 : borderWidth,
            hLineColor: () => borderColor, vLineColor: () => borderColor,
            paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
          },
          margin: [0, 0, 0, 0],
        };
        if (bottomPanelHeight && bottomPanelHeight > 0) {
          bottomTable.table.heights = [bottomPanelHeight];
        }
        elems.push(bottomTable);
      }

      return elems;
    };

    // ── Build header content — fresh deep-cloned objects for each page ─────────
    // pdfmake cannot share the same object instances across pages; we must
    // create independent copies by JSON-cloning the data + re-attaching layout fns.
    const headerSectionLayout = {
      hLineWidth: (i: number, node: any) => (i === node.table.body.length ? borderWidth : 0),
      vLineWidth: () => 0,
      hLineColor: () => borderColor,
      paddingLeft: () => 0, paddingRight: () => 0,
      paddingTop: () => 0, paddingBottom: () => 0,
    };

    const buildPageHeader = (): any[] => {
      const elems: any[] = [];

      // Invoice name: small centered title above the header on every page
      const invoiceNameTitle = buildInvoiceNameTitle(this.config, this.getFontForText(getInvoiceName(this.config)));
      if (invoiceNameTitle) elems.push(invoiceNameTitle);

      // Deep-clone the table body to get independent object instances per page
      const hSectionClone: any = {
        table: {
          widths: ['*'],
          heights: [headerSection.table.heights[0]],
          body: JSON.parse(JSON.stringify(headerSection.table.body)),
        },
        layout: headerSectionLayout,
        margin: [0, 0, 0, 0],
      };
      elems.push(hSectionClone);

      // partyInvoiceSection — deep clone data + re-attach layout
      if (partyInvoiceSection) {
        const piClone: any = {
          table: {
            widths: [...partyInvoiceSection.table.widths],
            heights: [...partyInvoiceSection.table.heights],
            body: JSON.parse(JSON.stringify(partyInvoiceSection.table.body)),
          },
          layout: partyInvoiceSection.layout,  // layout fns are stateless — safe to share
          margin: [0, 0, 0, 0],
        };
        elems.push(piClone);
      }

      return elems;
    };

    // ── Chunk items into pages of FIXED_ITEM_ROWS ──────────────────────────────
    const chunks: any[][] = [];
    for (let i = 0; i < Math.max(items.length, 1); i += FIXED_ITEM_ROWS) {
      chunks.push(items.slice(i, i + FIXED_ITEM_ROWS));
    }

    const totalPages = chunks.length;

    // ── Border layout (reused per page wrapper when showBorder) ───────────────
    const outerBorderLayout = {
      hLineWidth: () => borderWidth,
      vLineWidth: () => borderWidth,
      hLineColor: () => borderColor,
      vLineColor: () => borderColor,
      paddingLeft: () => 0, paddingRight: () => 0,
      paddingTop: () => 0, paddingBottom: () => 0,
    };

    // ── Assemble pages ────────────────────────────────────────────────────────
    const allPageElements: any[] = [];
    chunks.forEach((chunk, pageIdx) => {

      const isFirstPage = pageIdx === 0;
      const isLastPage = pageIdx === totalPages - 1;

      // Dynamically calculate bottom panel height on last page to stretch vertical dividers flush to bottom border
      let bottomPanelHeight: number | undefined;
      if (isLastPage) {
        const piH = partyInvoiceSection ? partyInvoiceSectionHeight : 0;
        let extraStripsH = 0;
        const totalsCfg = (this.config?.totalsSection || (this.config as any)?.totals || {}) as any;
        const showAmountInWords = totalsCfg?.showAmountInWords !== false;
        const rawInWords = data.amountInWords || (totalNetAmount > 0 ? this.numberToWords(totalNetAmount) : '');
        if (showAmountInWords && rawInWords) extraStripsH += 13;

        const showNotes = footer.showNotes === true;
        const notesText = rawData.notesText || rawData.notes || footer.notesText || '';
        if (showNotes && notesText) extraStripsH += 13;

        const topSectionsH = headerSectionHeight + invoiceNameTitleHeight(this.config) + piH + 145 + 16 + extraStripsH;
        bottomPanelHeight = Math.max(35, Math.floor(printableHeight - topSectionsH - 8));
      }

      // Build this page's stack of elements
      const pageElems: any[] = [
        ...buildPageHeader(),
        buildChunkTable(chunk, pageIdx * FIXED_ITEM_ROWS, isLastPage),
        ...(isLastPage ? buildFooterElements(bottomPanelHeight) : []),
      ];

      if (showBorder) {
        // Each page gets its own full-page border table
        const borderBlock: any = {
          table: {
            widths: [printableWidth - 2],
            heights: [printableHeight - 2],
            body: [[{ stack: pageElems, margin: [0, 0, 0, 0] }]],
          },
          layout: outerBorderLayout,
          margin: [0, 0, 0, 0],
        };
        if (!isFirstPage) borderBlock.pageBreak = 'before';
        allPageElements.push(borderBlock);
      } else {
        if (!isFirstPage) pageElems[0].pageBreak = 'before';
        pageElems.forEach(e => allPageElements.push(e));
      }
    });

    docContent = allPageElements;

    return {
      pageSize: 'A5',
      pageOrientation: 'landscape',
      pageMargins: [margins.left, margins.top, margins.right, margins.bottom],
      defaultStyle: {
        font: 'NotoSansTamil',
        fontSize: 8,
      },
      styles: {
        th: {
          bold: true,
          fontSize: 8,
          color: '#1a202c',
        },
      },
      content: docContent,
    };
  }

  private numberToWords(num: number): string {
    if (!isFinite(num)) return '';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    const convertLessThanThousand = (n: number): string => {
      n = Math.floor(n);
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      const h = Math.floor(n / 100);
      const rem = n % 100;
      return ones[h] + ' Hundred' + (rem !== 0 ? ' ' + convertLessThanThousand(rem) : '');
    };

    let n = Math.floor(Math.abs(num));
    if (n === 0) return 'Zero';

    const parts: string[] = [];
    const crore = Math.floor(n / 10000000);
    if (crore) {
      parts.push(convertLessThanThousand(crore) + ' Crore');
      n = n % 10000000;
    }
    const lakh = Math.floor(n / 100000);
    if (lakh) {
      parts.push(convertLessThanThousand(lakh) + ' Lakh');
      n = n % 100000;
    }
    const thousand = Math.floor(n / 1000);
    if (thousand) {
      parts.push(convertLessThanThousand(thousand) + ' Thousand');
      n = n % 1000;
    }
    if (n) {
      parts.push(convertLessThanThousand(n));
    }
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }
}
